/* tslint:disable:no-console object-literal-sort-keys */

import joinUrl from 'url-join';
import ajax from './AjaxPromises';
import globalConfig from './config';
import { isFeatureEnable } from './features-enabling';
import {
  extractZoneFromElement,
  formatSupportMraidTag,
  getCreativeType,
  getFormatParams
} from './format-helper';
import { initMraidInfos } from './mraid-infos';
import { getCurrentRenderTime } from './render-time';
import { getClientClickCoordinates, getPercentBetween } from './tracks-helper';
import {
  generateUrlWithRegion,
  updateQueryStringParameters
} from './url-utils';
import { getWindowSize } from './window-size-utils';
import { OCSTrackingEvent } from './OCSTrackingEvent';
import { isInternalSdk, isWeb } from './environment-utils';
import { DebugLog } from './debug-log';

export const BROWSER_WEBVIEW_CREATED_EVENT = 'browser_webview_created';

let format: FormatName;
let redirectUrl: string;
let creativeType: string | void;
let adContext: AdContext;
// TODO TS : better type this
let formatConfig: any;
let formatIsClosed: boolean = false;
let lastInteraction: number = 0;
let limitTimeBetweenAction: number = 30; // ms
// stored event for mraid
let lastEvent: MouseEvent;
let nobodyCatchEventTimeout: number;
let isRagequitListenerSet: boolean = false;
let creativeDraggedFirstSent: boolean = false;
let customTrackSent: boolean = false;

export function initAnalytics(
  formatName: FormatName,
  config: any,
  context: AdContext,
  force: boolean = false
) {
  initMraidInfos(formatName, parseInt(context.campaignId));

  adContext = context;
  redirectUrl = config.clickUrl || config.url;
  format = formatName;
  formatConfig = {
    ...config,
    ...(force ? { analytics: { enable: true } } : {})
  };

  creativeType = getCreativeType(format, formatConfig.creative);

  // ogyOnCloseSystem is Ogury-specific; only register when on Ogury SDK to avoid MRAID addEventListener errors on external SDKs
  if (
    isFeatureEnable('rageQuitEvent') &&
    isInternalSdk() &&
    !isRagequitListenerSet
  ) {
    isRagequitListenerSet = true;
    window.mraid.addEventListener('ogyOnCloseSystem', function onCloseSystem() {
      window.mraid.removeEventListener('ogyOnCloseSystem', onCloseSystem);
      sendCreativeRagequitTrack();
    });
  }

  if (formatSupportMraidTag(format)) {
    const body = document.querySelector('body');
    if (body) {
      body.setAttribute('data-zone', 'format');
    }

    limitTimeBetweenAction = 130;
    window.addEventListener('click', storeEventForAnalytics, true);
  }
}

function endAnalytics() {
  // warning for setTimeouts if using in mraid formats
  if (formatSupportMraidTag(format)) {
    window.removeEventListener('click', storeEventForAnalytics, true);
  }
}

/**
 * rootStore event to send it anyway, even if mraid.open or mraid.close are not called
 */
export function storeEventForAnalytics(e: MouseEvent) {
  lastEvent = e;
  nobodyCatchEventTimeout = window.setTimeout(() => {
    DebugLog.log('Nobody catch the stored event, send as dry click');
    sendAnalyticsTrack(lastEvent);
  }, 130);
}

export function sendMraidAnalyticsTrack(
  closeSource?: string,
  accomplished: boolean = false,
  forceZone?: string,
  eventType: string = 'interaction'
) {
  setTimeout(() => {
    // cancel the auto send after delay
    clearTimeout(nobodyCatchEventTimeout);
    sendAnalyticsTrack(
      lastEvent,
      closeSource,
      accomplished,
      forceZone,
      eventType
    );
  }, 10);
}

export function setClickPositionInTrack(
  track: CreativeTrack,
  eventType: CreativeEvent,
  event: ClickEvent
): void {
  if (!event) {
    return;
  }

  const coordinates = getClientClickCoordinates(eventType, event);
  if (coordinates?.pos) {
    track.click_x = coordinates.pos.x;
    track.click_y = coordinates.pos.y;

    // @ts-ignore
    if (event?.view?.innerWidth) {
      track.click_pc_x = getPercentBetween(
        coordinates.pos.x,
        // @ts-ignore
        event.view.innerWidth
      );
    }
    // @ts-ignore
    if (event?.view?.innerHeight) {
      track.click_pc_y = getPercentBetween(
        coordinates.pos.y,
        // @ts-ignore
        event.view.innerHeight
      );
    }
  }
}

export function sendCreativeTrack(
  eventType: CreativeEvent,
  event?: ClickEvent
) {
  if (window.isOgyPreview) {
    return;
  }
  if (!formatConfig || !formatConfig.displayTrackers) {
    return;
  }

  const datas: CreativeTrack = {
    a_ex: adContext._appExchange,
    dsp: adContext.dsp,
    a_sdk:
      (window.MRAID_ENV && window.MRAID_ENV.sdkVersion) ||
      adContext.sdkVersion ||
      undefined,
    ak: adContext.apiKey,
    auid: adContext.adUnitId,
    e: eventType,
    imp: adContext.impressionId,
    oc_id: parseInt(adContext.campaignId),
    timer: getCurrentRenderTime() || undefined,
    u_id: adContext.userId,
    t_u: adContext.isTemporaryUser,
    u_os: adContext.deviceOs,
    u_tk: adContext.userInstanceToken,
    dmn: adContext.domain,
    pg: adContext.page,
    a_b: adContext.appBundle,
    cr_id: adContext.creativeId
  };

  if (eventType === 'client_click') {
    setClickPositionInTrack(datas, eventType, event);
  }

  DebugLog.log(`sendCreativeTrack: ${eventType}`, { datas });

  const url = updateQueryStringParameters(
    joinUrl(
      generateUrlWithRegion(
        globalConfig.FORMAT_ANALYTICS_ENDPOINT,
        adContext.region
      ),
      'creative'
    ),
    datas
  );
  new Image().src = url;
}

export function sendCreativeCloseButtonClick() {
  sendCreativeTrack('close_button_click');
}

export function sendCreativeOptoutTrack() {
  sendCreativeTrack('optout');
}

export function sendCreativeRagequitTrack() {
  sendCreativeTrack('ragequit');
}

export function sendCreativeClientClickTrack() {
  sendCreativeTrack('client_click');
}

export function sendCreativeDraggedTrack() {
  if (
    adContext.adUnitType === 'overlay_thumbnail' &&
    adContext.isAdUnitDraggable &&
    !creativeDraggedFirstSent
  ) {
    creativeDraggedFirstSent = true;
    sendCreativeTrack('dragged');
  }
}

export function sendCreativeAutoCloseTrack() {
  sendCreativeTrack('auto_close');
}

export function sendCreativeLoadInitTrack() {
  sendCreativeTrack('load_init');
}

export function sendAnalyticsTrack(
  // TODO TS better type this
  // event: MouseEvent | TouchEvent | object,
  event: any,
  closeSource?: string,
  accomplished: boolean = false,
  forceZone?: string,
  eventType: string = 'interaction',
  delayBeforeInteraction?: number,
  params: Object = {}
) {
  if (window.isOgyPreview) {
    return;
  }
  // after clic on continue on VFS
  if (formatIsClosed && closeSource === 'continue_button') {
    forceZone = 'wait_close';
    closeSource = undefined;
  }

  if (formatIsClosed && closeSource) {
    closeSource += '_already_close';
  }

  if (!shouldSendTrack(event, closeSource, eventType)) {
    return;
  }
  /* eslint-disable camelcase */
  const device = getWindowSize();
  const datas: any = {
    advert_id: adContext.impressionId,
    app_api_key: adContext.apiKey || 0,
    campaign_id: parseInt(adContext.campaignId),
    delay_before_interaction: delayBeforeInteraction
      ? delayBeforeInteraction
      : getCurrentRenderTime(),
    device,
    event: eventType,
    format,
    format_version: globalConfig.COMMIT_HASH,
    is_accomplished: accomplished,
    orientation: device.width > device.height ? 'landscape' : 'portrait',
    params: {
      ...getFormatParams(format, formatConfig, redirectUrl, adContext),
      ...params
    },
    sdk_version: (window.MRAID_ENV && window.MRAID_ENV.sdkVersion) || 'unknown',
    creative_id: adContext.creativeId,
    app_bundle: adContext.appBundle,
    app_ssp: (adContext._appExchange || '').toUpperCase()
  };
  if (closeSource) {
    datas.close_source = closeSource;
  }
  if (creativeType) {
    datas.creative_type = creativeType;
  }
  // detect TouchEvent
  if (event) {
    if (
      event.touches &&
      event.touches.length &&
      typeof event.touches[0].clientX !== 'undefined' &&
      typeof event.touches[0].clientY !== 'undefined'
    ) {
      datas.pos = {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY
      };
      datas.zone = forceZone || extractZoneFromElement(event.touches[0].target);
      datas.interaction = 'screen_touch';
    } else if (
      typeof event.clientX !== 'undefined' &&
      typeof event.clientY !== 'undefined'
    ) {
      // detect MouseEvent
      datas.pos = {
        x: event.clientX,
        y: event.clientY
      };
      datas.zone = forceZone || extractZoneFromElement(event.target);
      datas.interaction = 'screen_click';
    } else if (forceZone) {
      datas.zone = forceZone;
      datas.interaction = 'screen_click';
    } else if (eventType === 'interaction') {
      datas.zone = 'system_button';
      datas.interaction = 'system_button';
    }
  }
  /* eslint-enable camelcase */

  if (closeSource) {
    endAnalytics();
  }

  DebugLog.log('sendAnalyticsTrack', { datas });

  ajax
    .postJson(
      joinUrl(
        generateUrlWithRegion(
          globalConfig.MS_ADS_MONITORING_EVENTS_URL,
          adContext.region
        ),
        'track'
      ),
      datas
    )
    .then(res => DebugLog.log('sendAnalyticsTrack: event sent', res))
    .catch(console.error);
}

export function sendCreativeClientIgnoredClick(e: ClickEvent) {
  sendCreativeTrack('client_ignored_click', e);
}

// NOTE : Could crash at the ad load if we use rewrite local
export function sendOmidTrackingEvents(omidTrackingEvents: any): void {
  if (window.isOgyPreview) {
    return;
  }

  // send event 1/100
  if (Math.floor(Math.random() * 100) !== 1) {
    return;
  }

  const datas = {
    ...generateAnalyticsData(),
    ...omidTrackingEvents
  };

  datas.event = 'omid_tracking';

  DebugLog.log('sendOmidTrackingEvents', { datas });

  ajax
    .postJson(
      joinUrl(
        generateUrlWithRegion(
          globalConfig.MS_ADS_MONITORING_EVENTS_URL,
          adContext.region
        ),
        'omid_tracking'
      ),
      datas
    )
    .then(res => DebugLog.log('sendOmidTrackingEvents: event sent', res))
    .catch(console.error);
}

export function sendMraidDebugTracks(eventName: string) {
  if (isWeb()) return;
  sendCustomTrack(eventName);
}

export function sendCustomTrack(
  eventType: string,
  delayBeforeInteraction?: number,
  params?: object
) {
  sendAnalyticsTrack(
    {},
    undefined,
    false,
    undefined,
    eventType,
    delayBeforeInteraction,
    params
  );
}

function shouldSendTrack(
  event: Event,
  closeSource: string | void,
  eventType: string
) {
  if (!format) {
    DebugLog.log(
      'shouldSendTrack: Format is not initialized, call initAnalytics()'
    );
    return false;
  }

  if (!formatConfig) {
    DebugLog.log('shouldSendTrack: no format config');
    return false;
  }

  const debugForceAnalytics = isDebugMode();

  if (
    !isEventTypeAlwaysEnable(eventType) &&
    !debugForceAnalytics &&
    ((!formatConfig.analytics && !isAnalyticsForced()) ||
      (!formatConfig.analytics.enable && !isAnalyticsForced()))
  ) {
    DebugLog.log('shouldSendTrack: analytics not enabled on this campaign');
    return false;
  }
  if (!event) {
    DebugLog.log('shouldSendTrack: no event found');
    return false;
  }
  if (rejectDoubleClick(eventType)) {
    DebugLog.log('shouldSendTrack: double event ignored');
    return false;
  }
  if (closeSource) {
    formatIsClosed = true;
  }
  return true;
}

export function sendCreativeEngagementEvent() {
  ajax
    .get(formatConfig.creativeEngagementUrl)
    .then(res => DebugLog.log('sendCreativeEngagementEvent: event sent', res))
    .catch(console.error);
}

export function sendCreativeTrackingEvents(
  event: OCSTrackingEvent,
  parameters: object | undefined
): void {
  if (window.isOgyPreview) {
    return;
  }
  const datas: CreativeTrackingEvent = {
    e_campaign_id: adContext.campaignId,
    app: adContext.apiKey,
    app_bundle: adContext.appBundle,
    app_sdk_version:
      (window.MRAID_ENV && window.MRAID_ENV.sdkVersion) || 'unknown',
    advertiser_id: adContext.advertiserId,
    e_advert_id: adContext.impressionId,
    app_ssp: (adContext._appExchange || '').toUpperCase(),
    dsp: adContext.dsp,
    domain: adContext.domain,
    page: adContext.page,
    e_creative_id: adContext.creativeId,
    creative_event: event,
    ...parameters
  };

  DebugLog.log('sendCreativeTrackingEvents', { datas });

  const url = updateQueryStringParameters(
    joinUrl(
      generateUrlWithRegion(
        globalConfig.MS_ADS_MONITORING_EVENTS_URL,
        adContext.region
      ),
      'creative'
    ),
    datas
  );

  ajax
    .get(url)
    .then(res => DebugLog.log('sendCreativeTrackingEvents: event sent', res))
    .catch(console.error);
}

function isAnalyticsForced(): boolean {
  // 271047 : Force analytics for Trivia Crack to understand performance drop
  // 301966 : IOS - Force analytics for Easy brain to understand performance drop
  return adContext.apiKey === '301966' || adContext.apiKey === '271047';
}

export function rejectDoubleClick(eventType: string): boolean {
  if (eventType !== 'interaction') {
    return false;
  }
  const now = Date.now();
  if (now - lastInteraction < limitTimeBetweenAction) {
    return true;
  } else {
    lastInteraction = now;
    return false;
  }
}

export function checkAndSendNoUserIdTrack(): void {
  if (!adContext.userId || typeof adContext.userId !== 'string') {
    sendCustomTrack('error_no_user_id');
  }
}

export function getDisplayTrackUrl(eventType: string): string {
  if (window.isOgyPreview) {
    return '';
  }

  if (!formatConfig || !formatConfig.displayTrackers) {
    return '';
  }

  if (
    formatConfig.displayTrackers.inhouse === true ||
    eventType === 'display' ||
    eventType.indexOf('omid_') === 0 ||
    eventType.indexOf('[event]') === 0
  ) {
    const datas: object = {
      /* eslint-disable camelcase */
      e: eventType,
      a_ex: adContext._appExchange,
      dsp: adContext.dsp,
      a_b: adContext.appBundle,
      dmn: adContext.domain,
      auid: adContext.adUnitId,
      oc_id: parseInt(adContext.campaignId),
      ak: adContext.apiKey,
      imp: adContext.impressionId,
      a_sdk: (window.MRAID_ENV && window.MRAID_ENV.sdkVersion) || undefined,
      u_id: adContext.userId,
      u_os: adContext.deviceOs,
      cr_id: adContext.creativeId,
      pg: adContext.page
      /* eslint-enable camelcase */
    };

    const url = updateQueryStringParameters(
      joinUrl(
        generateUrlWithRegion(
          globalConfig.MS_ADS_MONITORING_EVENTS_URL,
          adContext.region
        ),
        'viewability'
      ),
      datas
    );

    return url;
  }
  return '';
}

function generateAnalyticsData() {
  /* eslint-disable camelcase */
  const device = getWindowSize();

  let app_ssp = (adContext._appExchange || '').toUpperCase();

  // In stats index, ogury ssp is in lowercase
  if (app_ssp === 'OGURY') {
    app_ssp = app_ssp.toLowerCase();
  }

  return {
    advert_id: adContext.impressionId,
    device,
    params: getFormatParams(format, formatConfig, redirectUrl, adContext),
    orientation: device.width > device.height ? 'landscape' : 'portrait',
    format,
    format_version: globalConfig.COMMIT_HASH,
    sdk_version: (window.MRAID_ENV && window.MRAID_ENV.sdkVersion) || 'unknown',
    campaign_id: parseInt(adContext.campaignId),
    app_api_key: adContext.apiKey,
    app_ssp,
    dsp: adContext.dsp
  };
  /* eslint-enable camelcase */
}

export function checkMraid(origin: string) {
  if (isDebugMode() && !customTrackSent) {
    if (typeof window.mraid === 'undefined') {
      sendMraidDebugTracks(`format_mraid_undefined_after_load_${origin}`);
      customTrackSent = true;
    }

    if (typeof window.mraid !== 'undefined' && window?.mraid?.test !== 'test') {
      sendMraidDebugTracks(`format_mraid_changed_after_load_${origin}`);
      customTrackSent = true;
    }
  }
}

export function attachMraidMarkers() {
  if (window.mraid && isDebugMode()) {
    window.mraid.test = 'test';
  }
}

function isDebugMode(): boolean {
  return adContext?.enableMraidDebugTracks;
}

export function isEventTypeAlwaysEnable(eventType: string) {
  const alwaysEnableTypes = [
    'android_edge_issue',
    'store_kit_', // starting with
    'base64_iframe',
    'ad_manager_click_url_invalid',
    'empty_tag'
  ];
  return alwaysEnableTypes.some(
    type => type === eventType || eventType.indexOf(type) > -1
  );
}
